import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Chips, type ChipOption } from '@/components/Chips/Chips';
import {
  LocationSearchInput,
  type LocationSuggestion,
} from '@/components/LocationSearchInput/LocationSearchInput';
import { RangeSlider } from '@/components/RangeSlider/RangeSlider';
import { SearchInput } from '@/components/SearchInput/SearchInput';
import { findSection } from '@/lib/cms/section-types';
import { getCmsDocument, type CmsPageLocale } from '@/lib/db/cms-pages';
import type { CatalogueTypeFilter } from '@/routes/public/DealsPage.data';
import { DEFAULT_FILTERS, type Filters } from '@/routes/public/DealsPage.url';

import './HeroCataloguePremium.css';

const HERO_PRICE_STEP = 50_000;
const HERO_AREA_STEP = 50;
const HERO_DISCOUNT_MIN = 0;
const HERO_DISCOUNT_MAX = 50;
const HERO_DISCOUNT_STEP = 5;
const HERO_SEARCH_DEBOUNCE_MS = 300;
const TYPE_FILTERS: CatalogueTypeFilter[] = ['all', 'ready', 'offPlan'];

export type HeroCataloguePremiumProps = {
  filters: Filters;
  setFilters: (next: Filters) => void;
  searchDraft: string;
  setSearchDraft: (next: string) => void;
  isStatsLoading: boolean;
  headerCount: number;
  headerAverageDiscount: number | null;
  headerUpdatedRelative: string | null;
  /** Unified location autocomplete (addendum ADR-045 §8 — text-first search).
   * The committed value is the active location text ('' when no location
   * filter is active); picking a suggestion or submitting free text applies an
   * ILIKE filter on the deal zone/community/building columns upstream. */
  locationValue: string;
  locationSuggestionsFor: (query: string) => LocationSuggestion[];
  onLocationSelect: (suggestion: LocationSuggestion) => void;
  onLocationSubmit: (text: string) => void;
  onLocationClear: () => void;
  bedroomChips: ChipOption[];
  /** Property-type chips shared between the hero search row and the
   * post-hero toolbar. State is lifted to the parent (`filters.propertyType`)
   * so both entry points stay in sync, and URL ↔ DB round-trip is handled by
   * `DealsPage.url` + `searchPublishedDeals` (ADR-027). */
  propertyTypeChips: ChipOption[];
  propertyTypes: string[];
  setPropertyTypes: (next: string[]) => void;
  liveSuggestions: string[] | undefined;
  onSubmitScrollTo: () => void;
};

export function HeroCataloguePremium(props: HeroCataloguePremiumProps) {
  const {
    filters,
    setFilters,
    searchDraft,
    setSearchDraft,
    isStatsLoading,
    headerCount,
    headerAverageDiscount,
    headerUpdatedRelative,
    locationValue,
    locationSuggestionsFor,
    onLocationSelect,
    onLocationSubmit,
    onLocationClear,
    bedroomChips,
    propertyTypeChips,
    propertyTypes,
    setPropertyTypes,
    liveSuggestions,
    onSubmitScrollTo,
  } = props;
  const { t, i18n } = useTranslation();
  const [imageMissing, setImageMissing] = useState(false);

  const locale = (
    ['en', 'fr', 'ar'].includes(i18n.language?.split('-')[0] ?? '')
      ? (i18n.language.split('-')[0] as CmsPageLocale)
      : 'en'
  ) as CmsPageLocale;

  const [cmsHero, setCmsHero] = useState<{
    line1: string;
    line2: string;
    lead: string;
    imageUrl: string;
  } | null>(null);

  useEffect(() => {
    getCmsDocument('catalogue_hero', locale)
      .then((doc) => {
        const hero = findSection(doc, 'hero');
        if (hero && hero.visible)
          setCmsHero({
            line1: hero.line1,
            line2: hero.line2,
            lead: hero.lead,
            imageUrl: hero.image_url,
          });
      })
      .catch(() => {});
  }, [locale]);

  // CMS copy is strict: nothing renders while the document loads, when the
  // row/section is absent or hidden, or when the admin emptied a field — no
  // hardcoded fallback may resurrect content the CMS does not provide.
  const heroLine1 = cmsHero?.line1 ?? '';
  const heroLine2 = cmsHero?.line2 ?? '';
  const heroLead = cmsHero?.lead ?? '';
  const heroImage = cmsHero?.imageUrl ?? '';

  const typeChips: ChipOption[] = TYPE_FILTERS.map((tf) => ({
    value: tf,
    label: t(`catalogue.filters.${tf}`),
  }));

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmitScrollTo();
  };

  const handleNumberInput =
    (key: 'priceMin' | 'priceMax' | 'areaMin' | 'areaMax') =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;
      if (raw === '') {
        setFilters({ ...filters, [key]: DEFAULT_FILTERS[key] });
        return;
      }
      const n = Number(raw);
      if (Number.isFinite(n) && n >= 0) {
        setFilters({ ...filters, [key]: n });
      }
    };

  // `headerAverageDiscount` from useCatalogueStats is a positive percentage
  // (2026-05-16 convention realignment). We display it with the typographic
  // minus prefix; Math.abs stays defensive — guards any stale negative value
  // still in DB to avoid "−−11%".
  const discountValue =
    headerAverageDiscount !== null ? Math.abs(Math.round(headerAverageDiscount)) : null;

  return (
    <section className="hero-catalogue" aria-labelledby="hero-catalogue-title">
      {heroImage ? (
        <img
          className={`hero-catalogue__media${imageMissing ? ' hero-catalogue__media--missing' : ''}`}
          src={heroImage}
          alt=""
          aria-hidden="true"
          onError={() => setImageMissing(true)}
          loading="eager"
        />
      ) : null}
      <div className="hero-catalogue__overlay" aria-hidden="true" />

      <div className="hero-catalogue__inner">
        <div className="hero-catalogue__copy">
          {/* The h1 stays mounted — `aria-labelledby` above points at it. */}
          <h1 id="hero-catalogue-title" className="hero-catalogue__title">
            {heroLine1 && <span className="hero-catalogue__title-line1">{heroLine1}</span>}
            {heroLine2 && <span className="hero-catalogue__title-line2">{heroLine2}</span>}
          </h1>
          {heroLead && <p className="hero-catalogue__lead">{heroLead}</p>}
        </div>

        <form
          className="hero-catalogue__search"
          onSubmit={handleSubmit}
          aria-label={t('catalogue.hero.searchAria')}
        >
          <div className="hero-catalogue__search-row hero-catalogue__search-row--4cols">
            {/* Unified L1 location autocomplete — one field searches buildings,
             * communities and zones at once; suggestions carry a type badge
             * and picking one applies the matching canonical key filter. The
             * Keyword field below stays separate (free-text on title /
             * building / description). */}
            <div className="hero-catalogue__field">
              <label className="hero-catalogue__field-label">
                {t('catalogue.hero.fields.location')}
              </label>
              <LocationSearchInput
                ariaLabel={t('catalogue.filters.locationAria')}
                clearAriaLabel={t('catalogue.filters.locationClearAria')}
                placeholder={t('catalogue.filters.locationPlaceholder')}
                value={locationValue}
                suggestionsFor={locationSuggestionsFor}
                onSelect={onLocationSelect}
                onSubmitText={onLocationSubmit}
                onClear={onLocationClear}
                badgeLabels={{
                  building: t('catalogue.hero.fields.building'),
                  community: t('catalogue.hero.fields.community'),
                  zone: t('catalogue.hero.fields.zone'),
                }}
                freeTextLabel={(q) => t('catalogue.filters.locationFreeText', { q })}
              />
            </div>

            <div className="hero-catalogue__field">
              <label className="hero-catalogue__field-label">
                {t('catalogue.hero.fields.propertyType')}
              </label>
              <Chips
                ariaLabel={t('catalogue.filters.propertyTypeAria')}
                options={propertyTypeChips}
                multi
                value={propertyTypes}
                onChange={setPropertyTypes}
              />
            </div>

            <div className="hero-catalogue__field">
              <label className="hero-catalogue__field-label">
                {t('catalogue.hero.fields.type')}
              </label>
              <Chips
                ariaLabel={t('catalogue.hero.fields.typeAria')}
                options={typeChips}
                value={filters.typeFilter}
                onChange={(picked) =>
                  setFilters({
                    ...filters,
                    typeFilter: (picked ?? 'all') as CatalogueTypeFilter,
                  })
                }
              />
            </div>

            <div className="hero-catalogue__field">
              <label className="hero-catalogue__field-label">
                {t('catalogue.hero.fields.bedrooms')}
              </label>
              <Chips
                ariaLabel={t('catalogue.filters.bedroomsAria')}
                options={bedroomChips}
                multi
                value={filters.bedrooms}
                onChange={(bedrooms) => setFilters({ ...filters, bedrooms })}
              />
            </div>
          </div>

          <div className="hero-catalogue__search-row">
            <div className="hero-catalogue__field">
              <label className="hero-catalogue__field-label">
                {t('catalogue.hero.fields.price')}
              </label>
              <div className="hero-catalogue__range">
                <input
                  type="number"
                  inputMode="numeric"
                  step={HERO_PRICE_STEP}
                  value={filters.priceMin === DEFAULT_FILTERS.priceMin ? '' : filters.priceMin}
                  onChange={handleNumberInput('priceMin')}
                  aria-label={t('catalogue.filters.priceMin')}
                  placeholder={t('catalogue.filters.priceMin')}
                  className="hero-catalogue__num-input"
                />
                <span aria-hidden="true" className="hero-catalogue__range-sep">
                  —
                </span>
                <input
                  type="number"
                  inputMode="numeric"
                  step={HERO_PRICE_STEP}
                  value={filters.priceMax === DEFAULT_FILTERS.priceMax ? '' : filters.priceMax}
                  onChange={handleNumberInput('priceMax')}
                  aria-label={t('catalogue.filters.priceMax')}
                  placeholder={t('catalogue.filters.priceMax')}
                  className="hero-catalogue__num-input"
                />
              </div>
            </div>

            <div className="hero-catalogue__field">
              <label className="hero-catalogue__field-label">
                {t('catalogue.hero.fields.area')}
              </label>
              <div className="hero-catalogue__range">
                <input
                  type="number"
                  inputMode="numeric"
                  step={HERO_AREA_STEP}
                  value={filters.areaMin === DEFAULT_FILTERS.areaMin ? '' : filters.areaMin}
                  onChange={handleNumberInput('areaMin')}
                  aria-label={t('catalogue.filters.areaMin')}
                  placeholder={t('catalogue.filters.areaMin')}
                  className="hero-catalogue__num-input"
                />
                <span aria-hidden="true" className="hero-catalogue__range-sep">
                  —
                </span>
                <input
                  type="number"
                  inputMode="numeric"
                  step={HERO_AREA_STEP}
                  value={filters.areaMax === DEFAULT_FILTERS.areaMax ? '' : filters.areaMax}
                  onChange={handleNumberInput('areaMax')}
                  aria-label={t('catalogue.filters.areaMax')}
                  placeholder={t('catalogue.filters.areaMax')}
                  className="hero-catalogue__num-input"
                />
              </div>
            </div>

            <div className="hero-catalogue__field hero-catalogue__field--search">
              <label className="hero-catalogue__field-label">
                {t('catalogue.hero.fields.search')}
              </label>
              <SearchInput
                ariaLabel={t('catalogue.search.ariaLabel')}
                placeholder={t('catalogue.search.placeholder')}
                value={searchDraft}
                onChange={setSearchDraft}
                onDebouncedChange={(q) => setFilters({ ...filters, q })}
                suggestions={liveSuggestions}
                onSuggestionSelect={(q) => setFilters({ ...filters, q })}
                debounceMs={HERO_SEARCH_DEBOUNCE_MS}
              />
            </div>
          </div>

          <div className="hero-catalogue__search-row hero-catalogue__search-row--actions">
            <div className="hero-catalogue__field">
              <label className="hero-catalogue__field-label">
                {t('catalogue.filters.discountMin')} ·{' '}
                {t('catalogue.filters.discountMinValue', { value: filters.discountMin })}
              </label>
              <RangeSlider
                ariaLabelMin={t('catalogue.filters.discountMinAria')}
                ariaLabelMax={t('catalogue.filters.discountMinAria')}
                min={HERO_DISCOUNT_MIN}
                max={HERO_DISCOUNT_MAX}
                step={HERO_DISCOUNT_STEP}
                value={[filters.discountMin, HERO_DISCOUNT_MAX]}
                onChange={([discountMin]) => setFilters({ ...filters, discountMin })}
                format={(n) => `${n}%`}
              />
            </div>
            <button type="submit" className="hero-catalogue__cta">
              {t('catalogue.hero.cta')}
            </button>
          </div>
        </form>

        <div
          className="hero-catalogue__stats"
          role="group"
          aria-label={t('catalogue.hero.statsAria')}
          aria-busy={isStatsLoading}
        >
          <HeroStatItem
            big={isStatsLoading ? null : String(headerCount)}
            label={t('catalogue.hero.stats.available')}
          />
          <HeroStatItem
            big={isStatsLoading || discountValue === null ? null : `−${discountValue}%`}
            label={t('catalogue.hero.stats.discount')}
          />
          <HeroStatItem
            wide
            big={
              isStatsLoading || !headerUpdatedRelative
                ? null
                : t('catalogue.hero.stats.updatedPrefix', { when: headerUpdatedRelative })
            }
            label={t('catalogue.hero.stats.freshness')}
          />
        </div>
      </div>
    </section>
  );
}

type HeroStatItemProps = {
  big: string | null;
  label: string;
  /** Freshness indicator: renders as a status line (not a hero number) and
   * spans the full row on mobile so "Updated X hours ago" never wraps badly. */
  wide?: boolean;
};

function HeroStatItem({ big, label, wide = false }: HeroStatItemProps) {
  return (
    <div className={`hero-catalogue__stat${wide ? ' hero-catalogue__stat--wide' : ''}`}>
      {big === null ? (
        <span className="hero-catalogue__stat-skeleton" aria-hidden="true" />
      ) : (
        <span className="hero-catalogue__stat-big">{big}</span>
      )}
      <span className="hero-catalogue__stat-label">{label}</span>
    </div>
  );
}
